import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useToast } from '../../context/ToastContext';
import {
  FiSearch, FiClock, FiRefreshCw, FiMessageSquare,
  FiThumbsUp, FiThumbsDown, FiAlertTriangle, FiX, FiHome, FiCheck,
} from 'react-icons/fi';
import '../commissionscan/CommissionScan.css';
import './homeowners.css';
import PreviewIcon from '../../assets/images/ViewDetailsIcon.svg';

const PAGE_SIZE   = 10;
const getToken    = () => localStorage.getItem('toolboxToken') || '';
const getAmsToken = () => localStorage.getItem('amsToken')     || '';

const CUTOFF_DATE = new Date('2025-11-13');

const CONSTRUCTION_TYPES = [
  'Frame','Masonry','Masonry Veneer','Superior','Log','Metal',
  'Manufactured Housing','Modular','Other',
];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY',
];

const formatDate = (d: string): string => {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${m[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
  } catch { return d; }
};

interface HistoryRow {
  transactionId: string;
  insuredName: string;
  userName: string;
  property: string;
  homeOwnersResponse: string;
  submittedJsonData: string;
  isUpdateInAMS: boolean;
  isLiked: boolean | null;
  created: string;
  totalCount: number;
}

interface PropertyAddress {
  Address?: string;
  City?: string;
  PostalCode?: string;
  Province?: string;
  Country?: string;
}

interface HomeOwnerFields {
  constructionType?: string;
  occupancy?: string;
  residenceType?: string;
  usageType?: string;
  yearBuilt?: string;
  room?: string;
  families?: string;
  marketValue?: string;
  replacementCost?: string;
  totalLivingArea?: string;
  firePlaces?: string;
  rating?: string;
  roofMaterial?: string;
  primaryHeat?: string;
  foundation?: string;
}

const HomeOwnersHistory: React.FC = () => {
  const [rows, setRows]             = useState<HistoryRow[]>([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [search, setSearch]         = useState('');
  const [page, setPage]             = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  /* Preview modal */
  const [previewRow, setPreviewRow] = useState<HistoryRow | null>(null);
  const [previewFields, setPreviewFields] = useState<HomeOwnerFields>({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  /* Preview — property address fields (populated when !isUpdateInAMS) */
  const [prevBuildingId,  setPrevBuildingId]  = useState('');
  const [prevInsuredId,   setPrevInsuredId]   = useState('');
  const [prevAddress,     setPrevAddress]     = useState('');
  const [prevCity,        setPrevCity]        = useState('');
  const [prevState,       setPrevState]       = useState('');
  const [prevPostalCode,  setPrevPostalCode]  = useState('');
  const [prevCountry,     setPrevCountry]     = useState('');
  const [prevClientName,  setPrevClientName]  = useState('');

  /* Feedback popup */
  const [feedbackRowId,      setFeedbackRowId]      = useState<string | null>(null);
  const [feedbackText,       setFeedbackText]        = useState('');
  const [feedbackRating,     setFeedbackRating]      = useState<boolean | null>(null);
  const [feedbackAnchor,     setFeedbackAnchor]      = useState<{ top: number; right: number } | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting]  = useState(false);
  const [feedbackError,      setFeedbackError]       = useState('');
  const feedbackRef = useRef<HTMLDivElement>(null);

  /* Toast */
  const { showSuccess, showError } = useToast();

  /* ── Fetch ── */
  const fetchData = useCallback(async (pageNum: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${(process.env.REACT_APP_DataFabric_Url  || '')}/api/HomeOwners/homeOwnersTransactions?pageNumber=${pageNum}&recordCount=${PAGE_SIZE}`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      const data: HistoryRow[] = Array.isArray(res.data) ? res.data : [];
      setRows(data);
      setTotalCount(data.length > 0 ? (data[0].totalCount ?? data.length) : 0);
    } catch (e: any) {
      setError(e.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(page); }, [fetchData, page]);
  useEffect(() => { setPage(1); }, [search]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r => {
      let addr = '';
      try { addr = JSON.stringify(JSON.parse(r.property)); } catch { addr = r.property; }
      return (
        r.insuredName?.toLowerCase().includes(q) ||
        r.userName?.toLowerCase().includes(q) ||
        addr.toLowerCase().includes(q)
      );
    });
  }, [rows, search]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pageList = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
    .reduce<(number | '…')[]>((acc, p, idx, arr) => {
      if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('…');
      acc.push(p);
      return acc;
    }, []);

  /* ── Preview ── */
  const openPreview = useCallback((row: HistoryRow) => {
    setPreviewRow(row);
    setSubmitSuccess(false);

    /* Reset property address fields */
    setPrevBuildingId(''); setPrevInsuredId(''); setPrevAddress('');
    setPrevCity(''); setPrevState(''); setPrevPostalCode('');
    setPrevCountry(''); setPrevClientName('');

    try {
      const homeOwnersResponse = row.homeOwnersResponse ? JSON.parse(row.homeOwnersResponse) : {};
      const submittedJsonData  = row.submittedJsonData  ? JSON.parse(row.submittedJsonData)  : {};
      const property           = row.property           ? JSON.parse(row.property)           : {};

      if (row.isUpdateInAMS) {
        setPreviewFields({
          constructionType: submittedJsonData.constructionType || '',
          occupancy:        submittedJsonData.occupancy        || '',
          residenceType:    submittedJsonData.residenceType    || '',
          usageType:        submittedJsonData.usageType        || '',
          yearBuilt:        submittedJsonData.yearBuilt        || '',
          room:             submittedJsonData.room             || '',
          families:         submittedJsonData.families         || '',
          marketValue:      submittedJsonData.marketValue      || '',
          replacementCost:  submittedJsonData.replacementCost  || '',
          totalLivingArea:  submittedJsonData.totalLivingArea  || '',
          firePlaces:       submittedJsonData.firePlaces       || '',
          rating:           submittedJsonData.rating           || '',
          roofMaterial:     submittedJsonData.roofMaterial     || '',
          primaryHeat:      submittedJsonData.primaryHeat      || '',
          foundation:       submittedJsonData.foundation       || '',
        });
      } else {
        const rj = homeOwnersResponse.responseJson || {};
        setPreviewFields({
          constructionType: rj.ConstructionType || '',
          occupancy:        rj.Occupancy        || '',
          residenceType:    rj.ResidenceType    || '',
          usageType:        rj.UsageType        || '',
          yearBuilt:        rj.YearBuild        || '',
          room:             rj.Room             || '',
          families:         rj.Families         || '',
          marketValue:      rj.MarketValue      || '',
          replacementCost:  rj.ReplacementCost  || '',
          totalLivingArea:  rj.TotalLivingArea  || '',
          firePlaces:       rj.Fireplaces       || '',
          rating:           rj.Rating           || '',
          roofMaterial:     rj.RoofMaterial     || '',
          primaryHeat:      rj.PrimaryHeat      || '',
          foundation:       rj.Foundation       || '',
        });
        setPrevBuildingId(property.BuildingId   || '');
        setPrevInsuredId(property.InsuredId     || '');
        setPrevAddress(property.Address         || '');
        setPrevCity(property.City               || '');
        setPrevState(property.Province          || '');
        setPrevPostalCode(String(property.PostalCode ?? ''));
        setPrevCountry(property.Country         || '');
        setPrevClientName(property.InsuredName  || '');
      }
    } catch { /* malformed JSON — leave fields empty */ }
  }, []);

  const isEditable = useCallback((row: HistoryRow): boolean => {
    if (row.isUpdateInAMS) return false;
    return new Date(row.created) > CUTOFF_DATE;
  }, []);

  /* ── Re-submit from history ── */
  const onPreviewSubmit = useCallback(async () => {
    if (!previewRow) return;
    setSubmitLoading(true);
    try {
      const ncToken = getAmsToken();
      await axios.post(
        `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/addaccordeighty`,
        {
          insuredDatabaseId: prevInsuredId,
          buildingId: prevBuildingId,
          ...previewFields,
        },
        { params: { ncToken }, headers: { Authorization: `Bearer ${getToken()}` } },
      );
      await axios.post(
        `${(process.env.REACT_APP_DataFabric_Url  || '')}/api/HomeOwners/update`,
        { submittedJsonData: JSON.stringify(previewFields), transactionId: previewRow.transactionId },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      setSubmitSuccess(true);
      showSuccess('Submitted to NowCerts successfully');
      fetchData(page);
    } catch {
      showError('Failed to submit to NowCerts');
    } finally {
      setSubmitLoading(false);
    }
  }, [previewRow, previewFields, page, fetchData]);

  /* ── Feedback ── */
  const openFeedback = useCallback((row: HistoryRow, e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFeedbackRowId(row.transactionId);
    setFeedbackText('');
    setFeedbackRating(row.isLiked ?? null);
    setFeedbackError('');
    setFeedbackAnchor({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
  }, []);

  const closeFeedback = useCallback(() => {
    setFeedbackRowId(null);
    setFeedbackAnchor(null);
    setFeedbackText('');
    setFeedbackRating(null);
    setFeedbackError('');
  }, []);

  const saveRating = useCallback((id: string, isLiked: boolean | null) => {
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url  || '')}/api/HomeOwners/homeOwnersRatings`,
      { transactionId: id, IsLiked: isLiked },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    ).then(() => showSuccess('Feedback submitted successfully.')).catch(() => {});
  }, []);

  const onThumbsUp = useCallback(() => {
    if (!feedbackRowId) return;
    const cur = rows.find(r => r.transactionId === feedbackRowId);
    const next: boolean | null = cur?.isLiked === true ? null : true;
    setFeedbackRating(next);
    setRows(prev => prev.map(r => r.transactionId === feedbackRowId ? { ...r, isLiked: next } : r));
    saveRating(feedbackRowId, next);
    closeFeedback();
  }, [feedbackRowId, rows, saveRating, closeFeedback]);

  const onThumbsDown = useCallback(() => {
    if (!feedbackRowId) return;
    const cur = rows.find(r => r.transactionId === feedbackRowId);
    const next: boolean | null = cur?.isLiked === false ? null : false;
    setFeedbackRating(next);
    setRows(prev => prev.map(r => r.transactionId === feedbackRowId ? { ...r, isLiked: next } : r));
    saveRating(feedbackRowId, next);
    closeFeedback();
  }, [feedbackRowId, rows, saveRating, closeFeedback]);

  const onSubmitFeedback = useCallback(() => {
    if (!feedbackRowId) return;
    if (!feedbackText.trim()) { setFeedbackError('Please enter your feedback'); return; }
    if (feedbackText.length > 250) { setFeedbackError('Max 250 characters'); return; }
    setFeedbackError('');
    setFeedbackSubmitting(true);
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url  || '')}/api/HomeOwners/homeOwnersFeedback`,
      { transactionId: feedbackRowId, Feedback: feedbackText },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    )
      .then(() => { setFeedbackSubmitting(false); showSuccess('Feedback submitted'); closeFeedback(); })
      .catch(() => { setFeedbackSubmitting(false); showError('Failed to submit feedback'); });
  }, [feedbackRowId, feedbackText, closeFeedback]);

  useEffect(() => {
    if (!feedbackRowId) return;
    const handler = (e: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target as Node)) closeFeedback();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [feedbackRowId, closeFeedback]);

  const getPropertyDisplay = (propertyJson: string): string => {
    try {
      const p: PropertyAddress = JSON.parse(propertyJson);
      return [p.Address, p.City, p.Province].filter(Boolean).join(', ');
    } catch { return propertyJson || '—'; }
  };

  /* ════════════ RENDER ════════════ */
  return (
    <>
      <motion.div
        className="qp-history"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        {/* Header */}
        <div className="qp-history__header">
          <h3 className="qp-history__title" style={{ margin: 0 }}>Home Owner History</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="cs-client-search__input-wrap" style={{ minWidth: '220px' }}>
              <FiSearch size={13} className="cs-client-search__icon" />
              <input
                className="cs-client-search__input"
                type="text"
                placeholder="Search…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <motion.button
              className="csh-refresh-btn"
              onClick={() => fetchData(page)}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <FiRefreshCw size={13} className={loading && rows.length > 0 ? 'csh-spin' : ''} />
              Refresh
            </motion.button>
          </div>
        </div>

        {/* Body */}
        <div className="qp-history__body">
          {error ? (
            <div className="qp-empty-state" style={{ color: 'var(--accent-red)' }}>
              <FiClock size={28} /><p>{error}</p>
            </div>
          ) : loading ? (
            <div className="qp-history-loader" style={{ minHeight: 280 }}>
              <div className="qp-history-loader__orb">
                <div className="qp-history-loader__ring qp-history-loader__ring--1" />
                <div className="qp-history-loader__ring qp-history-loader__ring--2" />
                <div className="qp-history-loader__core"><FiHome size={16} /></div>
              </div>
              <div className="qp-history-loader__text">
                <p className="qp-history-loader__title">Loading History</p>
                <p className="qp-history-loader__sub">
                  Fetching home owner records
                  <span className="qp-init-loader__dots"><span>.</span><span>.</span><span>.</span></span>
                </p>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="qp-empty-state">
              <FiHome size={28} />
              <p>{search ? 'No records match your search.' : 'No home owner lookups yet.'}</p>
            </div>
          ) : (
            <div className="qp-table-wrap glass-surface">
              <table className="qp-table">
                <thead>
                  <tr>
                    <th className="qp-table__th">Insured Name</th>
                    <th className="qp-table__th">Property</th>
                    <th className="qp-table__th">Submitted Date</th>
                    <th className="qp-table__th">User</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Updated in AMS</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Action</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Feedback</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row, i) => {
                    const propertyDisplay = getPropertyDisplay(row.property);
                    return (
                      <tr key={row.transactionId ?? i} className="qp-table__row">
                        <td className="qp-table__td">{row.insuredName || '—'}</td>
                        <td className="qp-table__td">
                          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{propertyDisplay || '—'}</span>
                        </td>
                        <td className="qp-table__td" style={{ whiteSpace: 'nowrap' }}>{formatDate(row.created)}</td>
                        <td className="qp-table__td">{row.userName || '—'}</td>
                        <td className="qp-table__td" style={{ textAlign: 'center' }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                            background: row.isUpdateInAMS ? 'rgba(52,211,153,0.12)' : 'rgba(239,68,68,0.1)',
                            color:      row.isUpdateInAMS ? '#34d399'               : '#f87171',
                          }}>
                            {row.isUpdateInAMS ? '✓ Yes' : '✗ No'}
                          </span>
                        </td>
                        <td className="qp-table__td" style={{ textAlign: 'center' }}>
                          <button
                            className="qp-table__action-btn"
                            title="Preview"
                            onClick={() => openPreview(row)}
                          >
                            <img src={PreviewIcon} alt="Preview" width={30} height={30} />
                          </button>
                        </td>
                        <td className="qp-table__td" style={{ textAlign: 'center' }}>
                          <button
                            className="qp-table__action-btn"
                            title="Feedback"
                            onClick={e => openFeedback(row, e)}
                            style={{ margin: '0 auto' }}
                          >
                            <FiMessageSquare size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalCount > 0 && (
            <div className="qp-pagination">
              <span className="qp-pagination__info">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
              </span>
              <div className="qp-pagination__controls">
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(1)} title="First">«</button>
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(p => p - 1)} title="Previous">‹</button>
                {pageList.map((p, i) =>
                  p === '…'
                    ? <span key={`e-${i}`} className="qp-pagination__ellipsis">…</span>
                    : <button
                        key={p}
                        className={`qp-pagination__btn${page === p ? ' qp-pagination__btn--active' : ''}`}
                        onClick={() => setPage(p as number)}
                      >{p}</button>
                )}
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} title="Next">›</button>
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(totalPages)} title="Last">»</button>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* ── Feedback popup (portalled) ── */}
      {feedbackRowId && feedbackAnchor && ReactDOM.createPortal(
        <div
          ref={feedbackRef}
          className="csfb-popup"
          style={{ top: feedbackAnchor.top, right: feedbackAnchor.right }}
        >
          <h4 className="csfb-title">Feedback</h4>
          <textarea
            className="csfb-textarea"
            placeholder="Enter your feedback here"
            value={feedbackText}
            onChange={e => setFeedbackText(e.target.value)}
            rows={4}
          />
          {feedbackError && (
            <span style={{ fontSize: 11, color: 'var(--accent-red, #ef4444)', marginTop: 4, display: 'block' }}>
              {feedbackError}
            </span>
          )}
          <div className="csfb-rating">
            <button
              className={`csfb-rating-btn${feedbackRating === true ? ' csfb-rating-btn--up' : ''}`}
              onClick={onThumbsUp}
              title="Thumbs up"
            >
              <FiThumbsUp size={20} />
            </button>
            <button
              className={`csfb-rating-btn${feedbackRating === false ? ' csfb-rating-btn--down' : ''}`}
              onClick={onThumbsDown}
              title="Thumbs down"
            >
              <FiThumbsDown size={20} />
            </button>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button className="cs-browse-btn cs-browse-btn--sm" onClick={onSubmitFeedback} disabled={feedbackSubmitting} style={{ flex: 1 }}>
              {feedbackSubmitting ? 'Submitting…' : 'Submit'}
            </button>
            <button
              onClick={closeFeedback}
              style={{
                flex: 1, padding: '7px 16px', fontSize: 12, fontWeight: 600,
                background: 'var(--surface-glass)', color: 'var(--text-secondary)',
                border: '1px solid var(--border-glass)', borderRadius: 8, cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>,
        document.body,
      )}

      {/* ── Preview / Re-edit modal (portalled) ── */}
      {previewRow && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'var(--modal-overlay, rgba(0,0,0,0.75))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setPreviewRow(null)}
        >
          <div
            className="ho-modal"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="ho-modal__header">
              <div className="ho-modal__title-row">
                <FiHome size={16} className="ho-modal__icon" />
                <h3 className="ho-modal__title">Property Details</h3>
                {(previewRow.isUpdateInAMS || submitSuccess) && (
                  <span className="ho-badge ho-badge--success"><FiCheck size={10} /> In AMS</span>
                )}
              </div>
              <button className="ho-modal__close" onClick={() => setPreviewRow(null)}>
                <FiX size={16} />
              </button>
            </div>

            {/* Fields */}
            <div className="ho-modal__body">
              {Object.keys(previewFields).length === 0 ? (
                <div className="qp-empty-state" style={{ padding: '32px 0' }}>
                  <FiAlertTriangle size={22} />
                  <p>No property data available for this record.</p>
                </div>
              ) : (
                <>
                  {/* Property detail fields */}
                  <div className="ho-form-grid">
                    <div className="ho-form-field">
                      <label className="ho-form-label">Construction Type</label>
                      <div className="ho-select-wrap">
                        <select
                          className="ho-select"
                          value={previewFields.constructionType || ''}
                          onChange={e => setPreviewFields(p => ({ ...p, constructionType: e.target.value }))}
                          disabled={!isEditable(previewRow)}
                        >
                          <option value="">Select Construction Type</option>
                          {CONSTRUCTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                    </div>
                    {([
                      ['occupancy',       'Occupancy'],
                      ['residenceType',   'Residence Type'],
                      ['usageType',       'Usage Type'],
                      ['yearBuilt',       'Year Built'],
                      ['room',            'Rooms'],
                      ['families',        'Families'],
                      ['marketValue',     'Market Value ($)'],
                      ['replacementCost', 'Replacement Cost ($)'],
                      ['totalLivingArea', 'Total Living Area'],
                      ['firePlaces',      'Fire Places'],
                      ['rating',          'Rating'],
                      ['roofMaterial',    'Roof Material'],
                      ['primaryHeat',     'Primary Heat'],
                      ['foundation',      'Foundation'],
                    ] as [keyof HomeOwnerFields, string][]).map(([key, label]) => (
                      <div className="ho-form-field" key={key}>
                        <label className="ho-form-label">{label}</label>
                        <input
                          className="ho-input"
                          value={previewFields[key] || ''}
                          placeholder={`Enter ${label}`}
                          onChange={e => setPreviewFields(p => ({ ...p, [key]: e.target.value }))}
                          disabled={!isEditable(previewRow)}
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="ho-modal__footer" style={{ justifyContent: 'flex-end' }}>
              <button
                onClick={() => setPreviewRow(null)}
                style={{
                  padding: '8px 20px', fontSize: 13, fontWeight: 600,
                  background: 'var(--surface-glass)', color: 'var(--text-secondary)',
                  border: '1px solid var(--border-glass)', borderRadius: 8, cursor: 'pointer',
                }}
              >
                Close
              </button>
              {isEditable(previewRow) && !submitSuccess && (
                <motion.button
                  className="ho-search-btn"
                  onClick={onPreviewSubmit}
                  disabled={submitLoading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {submitLoading ? 'Submitting…' : 'Submit to NowCerts'}
                </motion.button>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default HomeOwnersHistory;

